import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface Props extends Omit<React.ComponentProps<"input">, "value" | "onChange" | "type"> {
  value: number | null;
  onChange(value: number | null): void;
  min: number;
  max: number;
  decimals?: number;
  suffix?: string;
}

const parse = (text: string, min: number, max: number) => {
  if (text.trim() === "") return null;
  const value = Number(text.replace(",", "."));
  return Number.isFinite(value) && value >= min && value <= max ? value : null;
};

const format = (value: number | null, decimals: number) =>
  value == null ? "" : String(Number(value.toFixed(decimals)));

// A number field that keeps what is typed and reports a value only while it is a number
// within [min, max], and null otherwise; an out-of-range entry stays visible and marked.
const NumberInput: React.FC<Props> = ({
  value,
  onChange,
  min,
  max,
  decimals = 0,
  suffix,
  className,
  ...props
}) => {
  const [text, setText] = useState(() => format(value, decimals));

  useEffect(() => {
    setText((current) => (parse(current, min, max) === value ? current : format(value, decimals)));
  }, [value, min, max, decimals]);

  const invalid = text.trim() !== "" && parse(text, min, max) == null;

  return (
    <div className={cn("relative", className)}>
      <Input
        type="text"
        inputMode={decimals > 0 || min < 0 ? "decimal" : "numeric"}
        value={text}
        aria-invalid={invalid}
        className={cn("tabular-nums aria-invalid:border-destructive", suffix && "pr-9")}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parse(e.target.value, min, max));
        }}
        {...props}
      />
      {suffix && (
        <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  );
};

export default NumberInput;
